
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import TrialStatusBanner from "@/components/TrialStatusBanner";
import { useNovidades } from "@/hooks/useNovidades";
import { NovoBadge } from "@/components/NovoBadge";
import { 
  WandSparkles, 
  Coins, 
  Settings, 
  BarChart3, 
  Crown,
  Target,
  Calculator,
  Sparkles,
  TrendingUp,
  DollarSign,
  ShoppingBag,
  Smartphone,
  User,
  HelpCircle,
  LifeBuoy,
  CreditCard,
  Newspaper,
  Zap
} from "lucide-react";

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasNovaForPath, marcarLida, novidades, naoLidas } = useNovidades();

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              Carregando...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const dashboardCards = [
    {
      title: "Resultado Rio",
      description: "Consulte os resultados do jogo do bicho do Rio de Janeiro dos últimos dias e busque resultados anteriores",
      icon: BarChart3,
      path: "/resultados-bicho",
      color: "from-blue-500 to-indigo-500",
      bgColor: "bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20",
      borderColor: "border-blue-200 dark:border-blue-800",
      isPremium: false
    },
    {
      title: "Gerar Jogos com IA",
      description: "Use inteligência artificial para gerar palpites inteligentes para o jogo do bicho",
      icon: WandSparkles,
      path: "/gerar-jogos",
      color: "from-purple-500 to-pink-500",
      bgColor: "bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20",
      borderColor: "border-purple-200 dark:border-purple-800",
      isPremium: false
    },
    {
      title: "Puxadas do Bicho",
      description: "Descubra quais bichos puxam outros com base nas dezenas sorteadas",
      icon: Target,
      path: "/puxadas",
      color: "from-emerald-500 to-teal-500",
      bgColor: "bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/20 dark:to-teal-950/20",
      borderColor: "border-emerald-200 dark:border-emerald-800",
      isPremium: false
    },
    {
      title: "Loterias da Caixa",
      description: "Gere jogos otimizados para Mega-Sena, Quina, Lotofácil e outras loterias",
      icon: Coins,
      path: "/loterias",
      color: "from-green-500 to-emerald-500",
      bgColor: "bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20",
      borderColor: "border-green-200 dark:border-green-800",
      isPremium: false
    },
    {
      title: "Software Turbinado",
      description: "Sistema avançado de análise e geração de jogos com histórico de resultados",
      icon: Settings,
      path: "/software",
      color: "from-blue-500 to-cyan-500",
      bgColor: "bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/20 dark:to-cyan-950/20",
      borderColor: "border-blue-200 dark:border-blue-800",
      isPremium: true
    },
    {
      title: "Métodos IA Palpite Guru",
      description: "Acesse todos os métodos avançados com 8 técnicas especializadas para o Jogo do Bicho",
      icon: Target,
      path: "/metodos",
      color: "from-red-500 to-pink-500",
      bgColor: "bg-gradient-to-br from-red-50 to-pink-50 dark:from-red-950/20 dark:to-pink-950/20",
      borderColor: "border-red-200 dark:border-red-800",
      isPremium: false
    },
    {
      title: "Análise Estatística",
      description: "Visualize padrões e tendências dos resultados para tomar decisões informadas",
      icon: BarChart3,
      path: "/estatisticas",
      color: "from-orange-500 to-red-500",
      bgColor: "bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-950/20 dark:to-red-950/20",
      borderColor: "border-orange-200 dark:border-orange-800",
      isPremium: true
    },
    {
      title: "Calculadora de Custos",
      description: "Calcule o investimento necessário para seus jogos e maximize seus lucros",
      icon: Calculator,
      path: "/calculadora",
      color: "from-indigo-500 to-purple-500",
      bgColor: "bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/20 dark:to-purple-950/20",
      borderColor: "border-indigo-200 dark:border-indigo-800",
      isPremium: true
    },
    {
      title: "Resultados Premium",
      description: "Acesse resultados em tempo real e histórico completo de todas as loterias",
      icon: TrendingUp,
      path: "/resultados",
      color: "from-teal-500 to-green-500",
      bgColor: "bg-gradient-to-br from-teal-50 to-green-50 dark:from-teal-950/20 dark:to-green-950/20",
      borderColor: "border-teal-200 dark:border-teal-800",
      isPremium: true
    },
    {
      title: "Programa de Afiliados",
      description: "Ganhe 20% de comissão compartilhando nossa plataforma com seus contatos",
      icon: DollarSign,
      path: "/afiliados",
      color: "from-green-500 to-emerald-500",
      bgColor: "bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20",
      borderColor: "border-green-200 dark:border-green-800",
      isPremium: false
    },
    {
      title: "Loja IA Guru",
      description: "Produtos digitais, e-books e ferramentas selecionadas para apostadores",
      icon: ShoppingBag,
      path: "/loja",
      color: "from-fuchsia-500 to-pink-500",
      bgColor: "bg-gradient-to-br from-fuchsia-50 to-pink-50 dark:from-fuchsia-950/20 dark:to-pink-950/20",
      borderColor: "border-fuchsia-200 dark:border-fuchsia-800",
      isPremium: false
    },
    {
      title: "Instalar App (PWA)",
      description: "Instale o IA Palpite Guru no seu celular para acessar com 1 toque",
      icon: Smartphone,
      path: "/instalar",
      color: "from-sky-500 to-blue-500",
      bgColor: "bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-950/20 dark:to-blue-950/20",
      borderColor: "border-sky-200 dark:border-sky-800",
      isPremium: false
    },
    {
      title: "Minha Conta",
      description: "Gerencie seus dados, plano e preferências de assinatura",
      icon: User,
      path: "/minha-conta",
      color: "from-slate-500 to-zinc-600",
      bgColor: "bg-gradient-to-br from-slate-50 to-zinc-50 dark:from-slate-950/20 dark:to-zinc-950/20",
      borderColor: "border-slate-200 dark:border-slate-800",
      isPremium: false
    },
    {
      title: "Suporte",
      description: "Entre em contato com nossa equipe pelo WhatsApp ou ticket interno",
      icon: LifeBuoy,
      path: "/suporte",
      color: "from-rose-500 to-red-500",
      bgColor: "bg-gradient-to-br from-rose-50 to-red-50 dark:from-rose-950/20 dark:to-red-950/20",
      borderColor: "border-rose-200 dark:border-rose-800",
      isPremium: false
    },
    {
      title: "FAQ",
      description: "Perguntas frequentes sobre o sistema, planos e métodos do bicho",
      icon: HelpCircle,
      path: "/faq",
      color: "from-amber-500 to-yellow-500",
      bgColor: "bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950/20 dark:to-yellow-950/20",
      borderColor: "border-amber-200 dark:border-amber-800",
      isPremium: false
    },
    ...(user.plan === 'free' ? [{
      title: "Assinar Plano",
      description: "Desbloqueie todos os métodos premium e ferramentas avançadas",
      icon: CreditCard,
      path: "/planos",
      color: "from-yellow-500 to-orange-500",
      bgColor: "bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-950/20 dark:to-orange-950/20",
      borderColor: "border-yellow-200 dark:border-yellow-800",
      isPremium: false
    }] : [])
  ];

  const metodosList = [
    { slug: "centena-matadora", title: "🎯 Centena Matadora" },
    { slug: "cruz-dia", title: "🎯 Cruz do Dia" },
    { slug: "esquema-infalivel", title: "✅ Esquema Infalível" },
    { slug: "dezenas-premiadas", title: "🎲 Dezenas Premiadas" },
    { slug: "metodo-777", title: "🔢 Método 777" },
    { slug: "metodo-sequencia", title: "📈 Método Sequência" },
    { slug: "centena-bichos", title: "🦁 Centena Bichos" },
    { slug: "7-grupos", title: "📊 7 Grupos" },
    { slug: "centenas-viciadas", title: "🎯 Centenas Viciadas" },
    { slug: "bionumerico-stellar", title: "🌟 BioNumérico Stellar" },
    { slug: "numerologia-nome", title: "🎰 Numerologia do Nome" },
    { slug: "soma-chave-ouro", title: "🔑 Soma Chave de Ouro" },
    { slug: "calculadora-chave-ouro", title: "🧮 Calculadora Chave de Ouro" },
    { slug: "achar-centena-dia", title: "🔍 Achar Centena do Dia" },
    { slug: "sistema-ia-bicho", title: "🤖 Sistema IA Bicho" },
    { slug: "desdobramento-jb", title: "🎰 Desdobramento JB" },
  ];


  const handleCardClick = (path: string, isPremium: boolean) => {
    if (isPremium && user.plan === 'free') {
      navigate("/planos");
    } else {
      navigate(path);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent mb-4">
              Dashboard
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Bem-vindo de volta, {user.firstName}! Escolha uma ferramenta para começar a gerar seus palpites inteligentes.
            </p>
          </div>

          {/* Trial Status Banner */}
          <TrialStatusBanner />

          {/* Vitrine CTA */}
          <button
            onClick={() => navigate("/vitrine")}
            className="group w-full mb-6 relative overflow-hidden rounded-2xl border border-yellow-500/40 bg-gradient-to-r from-black via-zinc-900 to-black p-5 md:p-6 text-left shadow-lg hover:shadow-yellow-500/30 transition-all"
          >
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-yellow-500/20 rounded-full blur-3xl group-hover:bg-yellow-500/30 transition-colors" />
            <div className="relative z-10 flex items-center gap-4">
              <div className="hidden sm:flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-yellow-500 to-amber-500 text-black shrink-0">
                <Sparkles className="h-6 w-6" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg md:text-xl font-extrabold text-yellow-400">
                  ✨ Conheça TODAS as ferramentas do IA Palpite Guru
                </h3>
                <p className="text-sm text-zinc-300">
                  Vitrine completa com métodos, IA, estatísticas e muito mais.
                </p>
              </div>
              <span className="px-4 py-2 rounded-full bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold text-sm shrink-0 group-hover:scale-105 transition-transform">
                Explorar →
              </span>
            </div>
          </button>

          {/* Guru Chat — destaque */}
          <button
            onClick={() => navigate("/guru-chat")}
            className="group w-full mb-6 relative overflow-hidden rounded-2xl border border-amber-500/40 bg-gradient-to-r from-zinc-950 via-amber-950/40 to-zinc-950 p-5 md:p-6 text-left shadow-lg hover:shadow-amber-500/30 transition-all"
          >
            <div className="absolute -top-10 -left-10 w-40 h-40 bg-amber-500/20 rounded-full blur-3xl group-hover:bg-amber-500/30 transition-colors" />
            <div className="relative z-10 flex items-center gap-4">
              <div className="hidden sm:flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-yellow-600 text-zinc-900 shrink-0 shadow-[0_0_20px_-4px_rgba(245,158,11,0.7)]">
                <Sparkles className="h-6 w-6" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg md:text-xl font-extrabold text-amber-300 flex items-center gap-2">
                  🤖 Fale com O Guru
                  <span className="bg-amber-400 text-black text-[10px] font-extrabold px-1.5 py-0.5 rounded-full">NOVO</span>
                </h3>
                <p className="text-sm text-zinc-300">
                  IA conversacional especialista em Jogo do Bicho. Interprete sonhos, peça palpites e abra ferramentas com 1 clique.
                </p>
              </div>
              <span className="px-4 py-2 rounded-full bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-bold text-sm shrink-0 group-hover:scale-105 transition-transform">
                Consultar →
              </span>
            </div>
          </button>

          {/* Dashboard Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dashboardCards.map((card, index) => {
              const Icon = card.icon;
              const isLocked = card.isPremium && user.plan === 'free';
              
              return (
                <Card 
                  key={index}
                  className={`${card.bgColor} ${card.borderColor} border-2 hover:shadow-xl transition-all duration-300 cursor-pointer group ${
                    isLocked ? 'opacity-75' : 'hover:scale-105'
                  }`}
                  onClick={() => handleCardClick(card.path, card.isPremium)}
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <div className={`p-3 rounded-xl bg-gradient-to-r ${card.color} shadow-lg`}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      {card.isPremium && (
                        <div className="flex items-center gap-1">
                          {isLocked && <Crown className="h-4 w-4 text-yellow-500" />}
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                            isLocked 
                              ? 'bg-yellow-100 text-yellow-700' 
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {isLocked ? 'Premium' : 'Ativo'}
                          </span>
                        </div>
                      )}
                    </div>
                    <CardTitle className="text-lg group-hover:text-primary transition-colors flex items-center gap-2">
                      {card.title}
                      {hasNovaForPath(card.path) && <NovoBadge />}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm leading-relaxed">
                      {card.description}
                    </CardDescription>
                    <div className="mt-4">
                      <Button 
                        variant={isLocked ? "outline" : "default"}
                        className={`w-full ${
                          isLocked 
                            ? 'border-yellow-300 text-yellow-700 hover:bg-yellow-50' 
                            : 'bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90'
                        }`}
                      >
                        {isLocked ? (
                          <>
                            <Crown className="w-4 h-4 mr-2" />
                            Fazer Upgrade
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Acessar
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Novidades */}
          {novidades.filter(n => naoLidas.has(n.id)).length > 0 && (
            <div className="mt-10">
              <h3 className="text-2xl font-bold text-center mb-4 flex items-center justify-center gap-2">
                <Newspaper className="w-6 h-6 text-amber-500" /> Novidades
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {novidades.filter(n => naoLidas.has(n.id)).map(n => (
                  <Card
                    key={n.id}
                    className="border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950/20 dark:to-yellow-950/20 cursor-pointer hover:shadow-lg transition"
                    onClick={() => {
                      marcarLida(n.id);
                      if (n.link) navigate(n.link);
                    }}
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Sparkles className="w-4 h-4 text-amber-600" />
                        {n.titulo}
                        <NovoBadge />
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription>{n.descricao}</CardDescription>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Métodos & Técnicas */}
          <div className="mt-12">
            <h3 className="text-2xl font-bold text-center mb-6 flex items-center justify-center gap-2">
              <Zap className="w-6 h-6 text-primary" /> Métodos & Técnicas
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {metodosList.map((m) => {
                const path = `/metodos?method=${m.slug}`;
                const isNova = novidades.some(
                  (n) => naoLidas.has(n.id) && n.metodo_slug === m.slug
                );
                return (
                  <Card
                    key={m.slug}
                    className="border-2 hover:border-primary cursor-pointer hover:shadow-md transition relative"
                    onClick={() => {
                      if (user.plan === 'free' && m.slug !== 'cruz-dia' && m.slug !== 'numerologia-nome') {
                        navigate('/planos');
                      } else {
                        navigate(path);
                      }
                    }}
                  >
                    <CardContent className="p-3 text-center">
                      <p className="text-sm font-semibold">{m.title}</p>
                      {isNova && (
                        <div className="absolute -top-2 -right-2">
                          <NovoBadge />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {user.plan !== 'free' && (
            <div className="mt-12">
              <h3 className="text-2xl font-bold text-center mb-6">Resumo da Conta</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="text-center">
                  <CardContent className="pt-6">
                    <div className="text-3xl font-bold text-primary mb-2">∞</div>
                    <p className="text-sm text-muted-foreground">Palpites Ilimitados</p>
                  </CardContent>
                </Card>
                <Card className="text-center">
                  <CardContent className="pt-6">
                    <div className="text-3xl font-bold text-green-600 mb-2">✓</div>
                    <p className="text-sm text-muted-foreground">Todas as Funcionalidades</p>
                  </CardContent>
                </Card>
                <Card className="text-center">
                  <CardContent className="pt-6">
                    <div className="text-3xl font-bold text-blue-600 mb-2">24/7</div>
                    <p className="text-sm text-muted-foreground">Suporte Premium</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
